<template>
  <HyperCodeHeroOverviewSection
    :id="item.fields.id"
    :title="headline"
    :second-title="secondTitle"
    :buttons="buttons"
    :items="items"
    :color-mode="colorMode"
    :content-centered="item.fields.contentPosition === 'centered'"
    :lazyload="item.fields.lazyloadMedia"
    :media="mediaItem"
    v-bind="
      $ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'headline'
      })
    "
  >
    <template #rteText>
      <LibRichTextRenderer v-if="descriptionRte" :rich-text="descriptionRte" />
      <p v-else-if="plainDescription">{{ plainDescription }}</p>
    </template>
  </HyperCodeHeroOverviewSection>
</template>

<script setup lang="ts">
import { HeroOverviewSection as HyperCodeHeroOverviewSection } from '@hypercodestudio/basler-components';
import type { OverviewItem } from '@hypercodestudio/basler-components';

import { getLinkIconPosition } from '~/utils/getLinkIconPosition';
import { generateImageObject } from '~/utils/generateImageObject';
import type { IHeroOverviewSection } from '~~/lib/ContentfulService';
import { isEntryResolveError } from '~/utils/guards/isEntryResolveError';
import { mapHeroSubtitle } from '~/utils/mapHeroSubtitle';
import { mapHeroHeadline } from '~/utils/mapHeroHeadline';
import { heroOverviewImageWidths } from '~/components/global/constants';
import {
  toDeprecatedColorMode,
  useInjectedColorMode
} from '~/composables/useInjectedColorMode';
import { buildHeroOverviewImageOptions } from '~/utils/imagePresetRegistry';
import { useMedia } from '~/composables/useMedia';
import { isMediaType, type IMedia } from '~/utils/guards/isMediaType';
const { $ContentfulLivePreview } = useNuxtApp();

interface Props {
  item: IHeroOverviewSection;
}
const props = defineProps<Props>();

const headline = computed(() => mapHeroHeadline(props.item));
const secondTitle = computed(() => mapHeroSubtitle(props.item));

// Der Link Type beinhaltet aktuell nur einen Text. Hypercode wurde in Ticket #1260 darüber informiert.
const buttons = computed(() =>
  props.item.fields.links
    ?.filter((entry) => !isEntryResolveError(entry))
    ?.map((field) => ({
      text: field.fields.linkText ?? '',
      link: useBuildLinkInterface(field),
      size: field.fields.linkStyleSize,
      styleType: field.fields.linkStyle ?? 'default',
      iconName: field.fields.linkIcon,
      targetBlank: field.fields.linkTarget === '_blank',
      iconType: getLinkIconPosition(field.fields.linkIconPosition)
    }))
);

const resolveHeroOverviewMedia = (
  media: IHeroOverviewSection['fields']['media']
): IMedia | undefined => {
  if (!media) {
    return undefined;
  }

  if (
    isMediaType(media, 'image') ||
    isMediaType(media, 'video') ||
    isMediaType(media, 'animation') ||
    isMediaType(media, 'customHtml') ||
    isMediaType(media, 'codeBlock')
  ) {
    return media;
  }

  return undefined;
};

const mediaItem = await useMedia(
  resolveHeroOverviewMedia(props.item.fields.media),
  {
    keepAspectRatio: true
  }
);

const colorMode = toDeprecatedColorMode(useInjectedColorMode());

const descriptionRte = computed(() => props.item.fields.descriptionRte ?? '');
const plainDescription = computed(() => props.item.fields.description ?? '');

const items = computed<OverviewItem[]>(
  () =>
    props.item.fields.items
      ?.filter((entry) => !isEntryResolveError(entry))
      ?.map((item) => {
        const itemLink = useBuildLinkInterface(
          item.fields.externalLink ?? item.fields.internalPage
        );
        const media = generateImageObject(item.fields.image, {
          ...buildHeroOverviewImageOptions(heroOverviewImageWidths)
        });

        return {
          media,
          lazyload: true,
          targetBlank: item.fields.linkTarget === '_blank',
          title: item.fields.linkText,
          link: itemLink?.uri ? itemLink : undefined
        };
      }) ?? []
);
</script>
